import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { productKitService } from '../services/api';

function formatAmount(value) {
  if (value == null) return '—';
  return `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
}

function KitsLogPage() {
  const [loading, setLoading] = useState(true);
  const [kits, setKits] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await productKitService.getAll();
        if (cancelled) return;
        const list = Array.isArray(res.data?.results)
          ? res.data.results
          : Array.isArray(res.data)
          ? res.data
          : [];
        setKits(list);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    const out = [];
    (kits || []).forEach((kit) => {
      (kit.items || []).forEach((item) => {
        const qty = Number(item.quantity) || 0;
        const price = item.unit_price != null ? Number(item.unit_price) : null;
        const amount = price != null ? qty * price : null;
        out.push({
          key: `${kit.id}-${item.id}`,
          kitName: kit.name,
          kitActive: kit.is_active,
          itemName: item.product_name || '—',
          article: item.product_article || '—',
          itemType: item.item_type === 'product' ? 'Виріб' : 'Упаковка',
          quantity: qty,
          price,
          amount,
        });
      });
    });
    return out;
  }, [kits]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.kitName, r.itemName, r.article]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Журнал позицій комплектів
        </Typography>
        <TextField
          size="small"
          placeholder="Пошук"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Назва комплекту</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Назва позиції</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell align="right">Кількість в комплекті</TableCell>
              <TableCell align="right">Ціна з ПДВ</TableCell>
              <TableCell align="right">Сума</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    Немає позицій
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell>{r.kitName}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.kitActive ? 'Активний' : 'Неактивний'}
                      color={r.kitActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{r.itemName}</TableCell>
                  <TableCell>{r.article}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.itemType}
                      color={r.itemType === 'Виріб' ? 'primary' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{r.quantity}</TableCell>
                  <TableCell align="right">{formatAmount(r.price)}</TableCell>
                  <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default KitsLogPage;
